import { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  CreditCard,
  DollarSign,
  PieChart,
  Shield,
  Settings,
  LogOut,
  Menu,
  X
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export function AdminSidebarLayout() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    const raw = localStorage.getItem('admin_session');
    if (raw) {
      try {
        const session = JSON.parse(raw);
        if (session?.user?.email) setUserEmail(session.user.email);
      } catch (e) {}
    }
  }, []);

  const handleSignOut = async () => {
    try {
      localStorage.removeItem('admin_session');
      await supabase.auth.signOut();
      toast.success('Logged out successfully');
      navigate('/admin/login');
    } catch (error) {
      toast.error('Failed to logout');
    }
  };

  const navItems = [
    { name: 'Overview', to: '/admin/dashboard', icon: LayoutDashboard },
    { name: 'Tenants', to: '/admin/tenants', icon: Users },
    { name: 'Billing', to: '/admin/payments', icon: CreditCard },
    { name: 'Coupons', to: '/admin/coupons', icon: DollarSign },
    { name: 'Analytics', to: '/admin/analytics', icon: PieChart },
    { name: 'Audit Logs', to: '/admin/audit', icon: Shield },
    { name: 'Settings', to: '/admin/settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-slate-950 flex font-sans">
      {/* Mobile Sidebar Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/80 md:hidden backdrop-blur-sm"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed inset-y-0 left-0 z-50 w-72 bg-slate-900 border-r border-slate-800 transition-transform duration-300 md:translate-x-0 md:static flex flex-col",
        isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        {/* Header */}
        <div className="h-16 flex items-center justify-between px-6 border-b border-slate-800 shrink-0">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-xl bg-purple-500/20 border border-purple-500/30 flex items-center justify-center shrink-0 overflow-hidden">
              <img src="/images/avatars/ravan_ultimate.png" alt="RAVAN Ultimate" className="h-full w-full object-cover" />
            </div>
            <div>
              <h1 className="text-sm font-semibold tracking-widest uppercase text-foreground leading-none mb-0.5">RAVAN Master</h1>
              <p className="text-[10px] text-purple-400/80 font-medium tracking-widest uppercase">SaaS Dashboard</p>
            </div>
          </div>
          <button className="md:hidden text-muted-foreground hover:text-foreground" onClick={() => setIsMobileMenuOpen(false)}>
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto py-6 px-4 space-y-1">
          <p className="px-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-2">Main Menu</p>
          {navItems.map((item) => (
            <NavLink
              key={item.name}
              to={item.to}
              end={item.to === '/admin/dashboard'}
              onClick={() => setIsMobileMenuOpen(false)}
              className={({ isActive }) => cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 group",
                isActive 
                  ? "bg-purple-500/10 text-purple-400 border border-purple-500/20 shadow-[inset_0_0_12px_rgba(168,85,247,0.05)]" 
                  : "text-muted-foreground hover:text-foreground hover:bg-slate-800/50 border border-transparent"
              )}
            >
              {({ isActive }) => (
                <>
                  <item.icon className={cn("h-4 w-4 transition-transform group-hover:scale-110", isActive ? "text-purple-400" : "text-muted-foreground")} />
                  {item.name}
                </>
              )}
            </NavLink>
          ))}
        </div>

        {/* Footer profile */}
        <div className="p-4 border-t border-slate-800 shrink-0">
          <div className="flex items-center justify-between p-3 rounded-xl bg-slate-800/50 border border-slate-700/50">
            <div className="overflow-hidden">
              <p className="text-[11px] font-medium text-foreground truncate">{userEmail || 'Admin User'}</p>
              <p className="text-[9px] font-semibold uppercase tracking-widest text-muted-foreground mt-0.5">Master Admin</p>
            </div>
            <button 
              onClick={handleSignOut}
              className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-red-400 hover:bg-red-400/10 transition-colors"
              title="Sign Out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative">
        {/* Mobile top bar */}
        <header className="md:hidden h-16 flex items-center justify-between px-4 border-b border-slate-800 bg-slate-900 shrink-0">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-xl overflow-hidden shrink-0 border border-purple-500/30">
              <img src="/images/avatars/ravan_ultimate.png" alt="RAVAN" className="h-full w-full object-cover" />
            </div>
            <span className="text-sm font-semibold tracking-widest uppercase text-foreground">RAVAN Master</span>
          </div>
          <button className="text-muted-foreground hover:text-foreground" onClick={() => setIsMobileMenuOpen(true)}>
            <Menu className="h-5 w-5" />
          </button>
        </header>

        {/* Page Content Container */}
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
